import type {
  DeviceRequest,
  InspectionDeviceRecordRequest,
  InspectionResponse,
  InspectionScopeDeviceResponse,
  InspectionTransitionRequest,
  InspectionOfflineFieldSetResponse,
} from 'src/api/ProactApiClient';
import {ComplianceStateEnum, InspectionStateEnum} from 'src/api/ProactApiClient';
import {offlineDb} from 'src/offline/db';
import {storeSessionFile} from 'src/offline/files';
import {readSnapshotData} from 'src/offline/offlineQuery';
import type {EnqueueApplyLocalContext} from 'src/offline/outbox';
import type {LocalEntryState} from './entryComposition';
import {recordValuesToResponse} from './entryComposition';
import {inspectionSnapshotKeys} from './snapshotKeys';

const putSnapshot = (
  sessionId: string,
  key: string,
  data: unknown
): Promise<[string, string]> =>
  offlineDb.snapshots.put({
    key,
    sessionId,
    data,
    cachedAt: new Date().toISOString(),
  });

// Mirrors InspectionService offline: remaining = scoped - entered (devices with
// a recorded state); scopedDeviceCount stays as downloaded (record adds nothing).
const applyDeviceCountsToMeta = async (
  sessionId: string,
  inspectionId: number,
  devices: InspectionScopeDeviceResponse[]
): Promise<void> => {
  const meta = await readSnapshotData<InspectionResponse>(
    sessionId,
    inspectionSnapshotKeys.meta(inspectionId)
  );
  if (!meta) return;

  const countFor = (state: ComplianceStateEnum): number =>
    devices.filter(device => device.complianceState === state).length;
  const entered = devices.filter(
    device => device.complianceState != null
  ).length;

  await putSnapshot(sessionId, inspectionSnapshotKeys.meta(inspectionId), {
    ...meta,
    passCount: countFor(ComplianceStateEnum.Pass),
    failCount: countFor(ComplianceStateEnum.Fail),
    noAccessCount: countFor(ComplianceStateEnum.NoAccess),
    remainingCount: Math.max(0, meta.scopedDeviceCount - entered),
  });
};

export interface RecordDeviceApplierArgs {
  inspectionId: number;
  deviceId: number;
  request: InspectionDeviceRecordRequest;
}

// Writes recorded compliance/values/comments to the local entry snapshot and
// reflects the new compliance state on the scope-device row.
export const applyRecordDevice = async (
  args: RecordDeviceApplierArgs,
  ctx: EnqueueApplyLocalContext
): Promise<void> => {
  const {inspectionId, deviceId, request} = args;

  const fieldSets = await readSnapshotData<InspectionOfflineFieldSetResponse[]>(
    ctx.sessionId,
    inspectionSnapshotKeys.definitions(inspectionId)
  );
  const device = (
    await readSnapshotData<InspectionScopeDeviceResponse[]>(
      ctx.sessionId,
      inspectionSnapshotKeys.devices(inspectionId)
    )
  )?.find(current => current.deviceId === deviceId);
  const fieldSet = fieldSets?.find(
    set => set.equipmentTypeId === device?.equipmentTypeId
  );

  const existing = await readSnapshotData<LocalEntryState>(
    ctx.sessionId,
    inspectionSnapshotKeys.entry(inspectionId, deviceId)
  );

  const entry: LocalEntryState = {
    complianceState: request.complianceState,
    comments: request.comments,
    inspectedAt: new Date().toISOString(),
    values: recordValuesToResponse(request.values, fieldSet),
    attachments: existing?.attachments ?? [],
  };
  await putSnapshot(
    ctx.sessionId,
    inspectionSnapshotKeys.entry(inspectionId, deviceId),
    entry
  );

  const devices = await readSnapshotData<InspectionScopeDeviceResponse[]>(
    ctx.sessionId,
    inspectionSnapshotKeys.devices(inspectionId)
  );
  if (devices) {
    const updated = devices.map(current =>
      current.deviceId === deviceId
        ? {...current, complianceState: request.complianceState}
        : current
    );
    await putSnapshot(
      ctx.sessionId,
      inspectionSnapshotKeys.devices(inspectionId),
      updated
    );
    await applyDeviceCountsToMeta(ctx.sessionId, inspectionId, updated);
  }
};

export interface AddCustomDeviceApplierArgs {
  inspectionId: number;
  tempDeviceId: number;
  request: DeviceRequest;
  equipmentTypeName?: string;
}

// Appends a not-yet-synced custom device (temp negative id) to the scope list.
export const applyAddCustomDevice = async (
  args: AddCustomDeviceApplierArgs,
  ctx: EnqueueApplyLocalContext
): Promise<void> => {
  const {inspectionId, tempDeviceId, request, equipmentTypeName} = args;
  const devices =
    (await readSnapshotData<InspectionScopeDeviceResponse[]>(
      ctx.sessionId,
      inspectionSnapshotKeys.devices(inspectionId)
    )) ?? [];

  const appended: InspectionScopeDeviceResponse = {
    deviceId: tempDeviceId,
    name: request.name,
    serialNumber: request.serialNumber,
    building: request.building,
    floor: request.floor,
    room: request.room,
    equipmentTypeId: request.equipmentTypeId,
    equipmentTypeName,
    deviceIsDeleted: false,
    isVerified: false,
    inspectionDeviceId: undefined,
    complianceState: undefined,
  };
  const updated = [...devices, appended];
  await putSnapshot(
    ctx.sessionId,
    inspectionSnapshotKeys.devices(inspectionId),
    updated
  );

  const meta = await readSnapshotData<InspectionResponse>(
    ctx.sessionId,
    inspectionSnapshotKeys.meta(inspectionId)
  );
  if (meta) {
    await putSnapshot(ctx.sessionId, inspectionSnapshotKeys.meta(inspectionId), {
      ...meta,
      scopedDeviceCount: meta.scopedDeviceCount + 1,
    });
    await applyDeviceCountsToMeta(ctx.sessionId, inspectionId, updated);
  }
};

export interface AddAttachmentApplierArgs {
  inspectionId: number;
  deviceId: number;
  name: string;
  contentType: string;
  blob: Blob;
}

export interface AppendAttachmentArgs {
  inspectionId: number;
  deviceId: number;
  name: string;
}

// Stores the captured blob (refKey = the item's clientSubmissionId) and appends
// a photo reference to the device's local entry snapshot.
export const applyAddAttachment = async (
  args: AddAttachmentApplierArgs,
  ctx: EnqueueApplyLocalContext
): Promise<void> => {
  const {inspectionId, deviceId, name, contentType, blob} = args;

  await storeSessionFile({
    sessionId: ctx.sessionId,
    kind: 'captured',
    refKey: ctx.clientSubmissionId,
    name,
    contentType,
    blob,
  });

  await appendAttachmentToEntry({inspectionId, deviceId, name}, ctx);
};

// Snapshot-only half of applyAddAttachment; the blob is already stored, so
// replay after a rebuild re-appends the photo ref without re-inserting it.
export const appendAttachmentToEntry = async (
  args: AppendAttachmentArgs,
  ctx: EnqueueApplyLocalContext
): Promise<void> => {
  const {inspectionId, deviceId, name} = args;

  const existing =
    (await readSnapshotData<LocalEntryState>(
      ctx.sessionId,
      inspectionSnapshotKeys.entry(inspectionId, deviceId)
    )) ?? {values: [], attachments: []};

  const photo = {
    id: -Date.now(),
    name,
    contentUrl: '',
    key: ctx.clientSubmissionId,
    isEditable: false,
    attachmentLinkId: 0,
    attachmentType: 'InspectionDevice',
    creatorName: '',
    createdAt: new Date() as unknown as Date,
    updatedAt: new Date() as unknown as Date,
  };

  await putSnapshot(
    ctx.sessionId,
    inspectionSnapshotKeys.entry(inspectionId, deviceId),
    {...existing, attachments: [...existing.attachments, photo]}
  );
};

export const TRANSITION_TARGET: Record<string, InspectionStateEnum> = {
  start: InspectionStateEnum.InProgress,
  complete: InspectionStateEnum.Completed,
};

export interface TransitionApplierArgs {
  id: number;
  request: InspectionTransitionRequest;
}

// Reflects a queued start/complete on the local inspection meta snapshot.
export const applyTransition = async (
  args: TransitionApplierArgs,
  ctx: EnqueueApplyLocalContext
): Promise<void> => {
  const target = TRANSITION_TARGET[args.request.transition];
  if (target === undefined) return;

  const meta = await readSnapshotData<InspectionResponse>(
    ctx.sessionId,
    inspectionSnapshotKeys.meta(args.id)
  );
  if (!meta) return;

  await putSnapshot(ctx.sessionId, inspectionSnapshotKeys.meta(args.id), {
    ...meta,
    state: target,
    completedAt:
      target === InspectionStateEnum.Completed
        ? (new Date() as unknown as Date)
        : meta.completedAt,
  });
};
